import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, Stack, Typography } from '@mui/material';

import { Feature, Overlay } from 'ol';
import Map from 'ol/Map';
import View from 'ol/View';
import { defaults } from 'ol/control';
import { getCenter } from 'ol/extent';
import { Point } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import 'ol/ol.css';
import { Projection } from 'ol/proj';
import { ImageStatic } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { Icon, Style } from 'ol/style';

import { CommentResponse } from '@/api/data-contracts';
import { ReactComponent as CloseIcon } from '@/assets/icons/close-icon.svg';
import { CommentOrTaskForm } from '@/features/comments';
import { useComments } from '@/features/comments';
import { Popup } from '@/features/media/ui/FileManager/ActiveFile.styles';
import { useUsersService } from '@/features/users';

interface Props {
  activeFile: File;
  height: number;
  zoom: number;
  setZoom: Dispatch<SetStateAction<number>>;
  width: number;
  setWidth: Dispatch<SetStateAction<number>>;
  mediaPlanId: string | undefined;
  taskId: string | undefined;
  attachmentId: string | undefined;
  setCommentsThreadId: Dispatch<SetStateAction<string | undefined>>;
  onClickComment: () => void;
}

const ActiveFile: FC<Props> = ({
  activeFile,
  height,
  zoom,
  setZoom,
  width,
  setWidth,
  mediaPlanId,
  taskId,
  attachmentId,
  setCommentsThreadId,
  onClickComment,
}) => {
  const { t } = useTranslation();
  const { getUserAvatar } = useUsersService();
  const { commentThreads, saveComment } = useComments({ mediaPlanId, taskId, attachmentId });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const commentsPopupRef = useRef<HTMLDivElement | null>(null);
  const popupCoordinatesRef = useRef<number[] | null>(null);

  const [aspectRatio, setAspectRation] = useState<number>(0);
  const [commentsOverlay, setCommentsOverlay] = useState<Overlay>();

  const parentComments = useMemo<CommentResponse[]>(() => {
    return Object.entries(commentThreads ?? {})
      .reverse()
      .map(([, comments]) => comments)
      .flat()
      .filter((c) => !c.parentId && !!c.coordinates);
  }, [commentThreads]);

  const createCircularImage = useCallback(async (url: string, size: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image(); // Cream imaginea
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
          reject('Canvas context is not supported');
          return;
        }

        const scale = img.width > img.height ? img.height / size : img.width / size;
        const canvasSize = Math.max(img.width, img.height) / scale;

        canvas.width = canvasSize;
        canvas.height = canvasSize;

        context.beginPath();
        context.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, Math.PI * 2, true);
        context.closePath();
        context.clip();

        context.drawImage(img, 0, 0, canvasSize, canvasSize);

        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => reject('Failed to load image');
      img.src = url;
    });
  }, []);

  const extent = useMemo<number[] | undefined>(() => {
    if (!aspectRatio || !width) return;

    let extentWidth, extentHeight;
    if (width / height > aspectRatio) {
      extentHeight = height;
      extentWidth = extentHeight * aspectRatio;
    } else {
      extentWidth = width;
      extentHeight = extentWidth / aspectRatio;
    }

    return [0, 0, extentWidth * 2, extentHeight * 2];
  }, [aspectRatio, height, width]);

  const projection = useMemo(() => {
    if (!extent) return undefined;

    return new Projection({
      code: 'background-image',
      units: 'pixels',
      extent,
    });
  }, [extent]);

  const view = useMemo(() => {
    if (!projection || !extent) return undefined;

    return new View({
      projection: projection,
      center: getCenter(extent),
      zoom: 2,
      minZoom: 2,
      maxZoom: 6,
    });
  }, [extent, projection]);

  const map = useMemo(() => {
    if (!projection || !extent || !commentsOverlay || !view) return;

    return new Map({
      target: 'map-canvas',
      layers: [
        new ImageLayer({
          source: new ImageStatic({
            url: URL.createObjectURL(activeFile),
            projection: projection,
            imageExtent: extent,
          }),
        }),
      ],
      overlays: [commentsOverlay],
      view,
      controls: defaults({ zoom: false }),
    });
  }, [activeFile, commentsOverlay, extent, projection, view]);

  useEffect(() => {
    if (map) {
      map.on('moveend', () => {
        const newZoom = parseFloat((map!.getView().getZoom() ?? 1).toFixed(2));
        if (newZoom !== zoom) {
          setZoom(newZoom);
        }
      });
    }
  }, [map, setZoom, zoom]);

  useEffect(() => {
    if (map && commentsOverlay) {
      map.on('singleclick', (event) => {
        let markerClicked = false;

        map.forEachFeatureAtPixel(event.pixel, (feature) => {
          if (feature) {
            setCommentsThreadId(feature.get('parentId'));
            onClickComment();
            markerClicked = true;
          }
        });

        if (!markerClicked) {
          let [xCorrection, yCorrection] = [0, 0];
          const [x, y] = event.coordinate;
          const [x1, y1, x2, y2] = map.getView().calculateExtent();

          if (x - 50 < x1) {
            xCorrection = x1 + 50 - x;
          }

          if (x + 330 > x2) {
            xCorrection = -(x + 330 - x2);
          }

          if (y + 250 > y2) {
            yCorrection = y1 + 250 - y;
          }

          const xCenter = (x2 + x1) / 2;
          const yCenter = (y2 + y1) / 2;

          map.getView().setCenter([xCenter - xCorrection, yCenter - yCorrection]);

          popupCoordinatesRef.current = [x, y];
          commentsOverlay?.setPosition([x, y]);
        }
      });
    }
  }, [commentsOverlay, map, onClickComment, setCommentsThreadId]);

  useEffect(() => {
    if (map && view) {
      const currentZoom = parseFloat((map!.getView().getZoom() ?? 1).toFixed(2));
      if (currentZoom !== zoom) {
        view.setZoom(zoom);
      }
    }
  }, [map, view, zoom]);

  const drawMarker = useCallback(
    async (comment: CommentResponse) => {
      const marker = new Feature({
        geometry: new Point([comment.coordinates!.first, comment.coordinates!.second]),
        parentId: comment.id,
      });

      marker.setStyle(
        new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: await createCircularImage(
              URL.createObjectURL(await getUserAvatar(comment.author.id)),
              40,
            ),
            scale: 1,
            size: [40, 40],
          }),
        }),
      );

      const markerLayer = new VectorLayer({
        source: new VectorSource({
          features: [marker],
        }),
      });

      map!.addLayer(markerLayer);
    },
    [createCircularImage, getUserAvatar, map],
  );

  useEffect(() => {
    if (map && parentComments?.length) {
      parentComments?.forEach((comment: CommentResponse) => {
        drawMarker(comment).then();
      });
    }
  }, [drawMarker, map, parentComments, width]);

  useEffect(() => {
    const img = document.createElement('img');
    img.onload = function () {
      setAspectRation(img.width / img.height);
    };
    img.src = URL.createObjectURL(activeFile);
  }, [activeFile]);

  useEffect(() => {
    const element = containerRef.current;
    if (width || !element) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(element);
    return () => {
      resizeObserver.unobserve(element);
    };
  }, [setWidth, width]);

  useEffect(() => {
    return () => {
      map?.setTarget(undefined);
    };
  }, [map]);

  const onCommentSave = useCallback(
    (args: {
      message: string;
      files?: File[];
      commentId?: string;
      parentId?: string;
      edit?: boolean;
    }) => {
      saveComment({ ...args, coordinates: popupCoordinatesRef.current ?? undefined });
      commentsOverlay?.setPosition(undefined);
    },
    [commentsOverlay, saveComment],
  );

  useEffect(() => {
    const element = commentsPopupRef.current;
    if (!element) return;

    setCommentsOverlay(
      new Overlay({
        element,
      }),
    );
  }, []);

  return (
    <>
      {activeFile.name.includes('.mp4') ? (
        <video controls style={{ width: '100%', height }}>
          <source src={URL.createObjectURL(activeFile)} type="video/mp4" />
        </video>
      ) : (
        <div ref={containerRef} style={{ width: '100%', height }}>
          <div id="map-canvas" style={{ width, height, border: '1px solid #ccc' }} />
        </div>
      )}

      <div ref={commentsPopupRef}>
        {!!map && !!activeFile && (
          <Popup>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography variant={'h5'}>{t('Comment')}</Typography>
              <IconButton onClick={() => commentsOverlay?.setPosition(undefined)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <CommentOrTaskForm type="comment" onCommentSave={onCommentSave} />
          </Popup>
        )}
      </div>
    </>
  );
};

export default ActiveFile;
